import React, { useState } from 'react';
import { Card, Button, Space, Table, message, Divider, Tooltip, Row, Col, InputNumber, Progress } from 'antd';
import { CopyOutlined, DownloadOutlined, MinusOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface WalletInfo {
  index: number;
  address: string;
  privateKey: string;
  mnemonic: string;
}

const BatchWallet = () => {
  const [selectedChain, setSelectedChain] = useState<string>('EVM');
  const [mnemonicLength, setMnemonicLength] = useState<number>(12);
  const [processCount, setProcessCount] = useState<number>(5);
  const [walletCount, setWalletCount] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const [wallets, setWallets] = useState<WalletInfo[]>([]);
  const [progress, setProgress] = useState(0);
  const [generatedCount, setGeneratedCount] = useState(0);

  const chainOptions = [
    'EVM', 'SOL', 'BITCOIN', 'SUI', 'TRX', 'APTOS', 'BITCOIN TESTNET', 'COSMOS', 'TON'
  ];

  const mnemonicLengthOptions = [12, 15, 18, 21, 24];

  // 统一的组件样式
  const componentStyle = {
    height: '36px',
    borderRadius: '6px',
  };

  const buttonStyle = {
    ...componentStyle,
    minWidth: '80px',
  };

  const primaryButtonStyle = {
    ...componentStyle,
    minWidth: '120px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    color: 'white',
    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
  };

  const operationButtonStyle = {
    ...componentStyle,
    minWidth: '36px',
    width: '36px',
  };

  // 调整数值的函数
  const adjustValue = (
    currentValue: number, 
    adjustment: number, 
    min: number, 
    max: number, 
    setValue: (value: number) => void
  ) => {
    const newValue = currentValue + adjustment;
    if (newValue >= min && newValue <= max) {
      setValue(newValue);
    }
  };

  // 生成钱包 (使用 Web Workers 实现并行处理)
  const generateWallets = async () => {
    if (!['EVM', 'SOL', 'SUI', 'APTOS'].includes(selectedChain)) {
      message.warning(`当前仅支持 EVM、SOL、SUI 和 APTOS 钱包生成`);
      return;
    }

    if (!walletCount || walletCount < 1 || walletCount > 10000) {
      message.error('请输入有效的钱包数量 (1-10000)');
      return;
    }

    setLoading(true);
    setWallets([]); // 清空旧数据
    setProgress(0);
    setGeneratedCount(0);

    try {
      const workers: Worker[] = [];
      const promises: Promise<WalletInfo[]>[] = [];
      const walletsPerWorker = Math.ceil(walletCount / processCount); // 每个 Worker 处理的钱包数量
      
      // 用于跟踪每个 worker 的进度
      const workerProgress = new Array(processCount).fill(0);

      for (let i = 0; i < processCount; i++) {
        const countForThisWorker = Math.min(walletsPerWorker, walletCount - i * walletsPerWorker);
        if (countForThisWorker <= 0) {
          continue; // 如果没有任务，跳过
        }

        const worker = new Worker(new URL('../../walletGenerator.worker.js', import.meta.url));
        workers.push(worker);

        const startIndex = i * walletsPerWorker; // 为每个 Worker 计算起始索引
        const workerIndex = i;

        promises.push(
          new Promise((resolve, reject) => {
            worker.onmessage = (event) => {
              const { success, wallets: workerWallets, error, progress, completed } = event.data;
              
              if (success) {
                // 最终完成
                workerProgress[workerIndex] = countForThisWorker;
                const totalCompleted = workerProgress.reduce((sum, count) => sum + count, 0);
                const currentProgress = Math.round((totalCompleted / walletCount) * 100);
                setProgress(currentProgress);
                setGeneratedCount(totalCompleted);
                resolve(workerWallets);
                worker.terminate(); // 任务完成后终止 Worker
              } else if (progress) {
                // 进度更新
                workerProgress[workerIndex] = completed;
                const totalCompleted = workerProgress.reduce((sum, count) => sum + count, 0);
                const currentProgress = Math.round((totalCompleted / walletCount) * 100);
                setProgress(currentProgress);
                setGeneratedCount(totalCompleted);
                // 不 resolve，继续等待最终结果
              } else if (error) {
                reject(new Error(error));
                worker.terminate(); // 发生错误时终止 Worker
              }
            };
            
            worker.onerror = (error) => {
              reject(error);
              worker.terminate(); // 发生错误时终止 Worker
            };
            
            // 将任务发送给 Worker
            worker.postMessage({
              selectedChain,
              mnemonicLength,
              count: countForThisWorker,
              startIndex,
            });
          })
        );
      }

      const results = await Promise.all(promises);
      
      const allWallets = results.flat().sort((a, b) => a.index - b.index); // 合并并按索引排序
      setWallets(allWallets);
      setProgress(100);
      setGeneratedCount(walletCount);
      
      message.success(`成功生成 ${walletCount} 个 ${selectedChain} 钱包`);
    } catch (error) {
      message.error('生成钱包失败，请重试');
      console.error('Generate wallets error:', error);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setProgress(0);
        setGeneratedCount(0);
      }, 2000); // 2秒后重置进度
    }
  };

  // 复制到剪贴板
  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${type}已复制到剪贴板`);
    } catch (error) {
      message.error('复制失败');
    }
  };

  // 导出到Excel
  const exportToExcel = () => {
    if (wallets.length === 0) {
      message.error('没有钱包数据可导出');
      return;
    }

    try {
      const exportData = wallets.map(wallet => {
        const baseData = {
          '序号': wallet.index,
          '钱包地址': wallet.address,
          '私钥': wallet.privateKey,
        };
        
        // 只有EVM钱包导出助记词
        if (selectedChain === 'EVM') {
          return {
            ...baseData,
            '助记词': wallet.mnemonic
          };
        }
        
        return baseData;
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, `${selectedChain}钱包`);

      // 设置列宽
      const colWidths = [
        { wch: 8 },  // 序号
        { wch: 45 }, // 钱包地址
        { wch: 70 }, // 私钥 
      ];
      
      // EVM钱包添加助记词列宽
      if (selectedChain === 'EVM') {
        colWidths.push({ wch: 80 }); // 助记词
      }
      
      worksheet['!cols'] = colWidths;

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const fileName = `${selectedChain}钱包_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      saveAs(data, fileName);
      message.success('导出成功');
    } catch (error) {
      message.error('导出失败');
      console.error('Export error:', error);
    }
  };

  // 表格列配置
  const columns = [
    {
      title: '序号',
      dataIndex: 'index',
      key: 'index',
      width: 80,
      align: 'center' as const,
    },
    {
      title: '钱包地址',
      dataIndex: 'address',
      key: 'address',
      width: 350,
      render: (text: string) => (
        <Space>
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{text}</span>
          <Tooltip title="复制地址">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(text, '地址')}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '私钥',
      dataIndex: 'privateKey',
      key: 'privateKey',
      width: 350,
      render: (text: string) => (
        <Space>
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {text.slice(0, 16)}...{text.slice(-16)}
          </span>
          <Tooltip title="复制私钥">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(text, '私钥')}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '助记词',
      dataIndex: 'mnemonic',
      key: 'mnemonic',
      render: (text: string) => (
        selectedChain === 'EVM' ? (
          <Space>
            <span style={{ fontSize: '12px' }}>
              {text.slice(0, 25)}...
            </span>
            <Tooltip title="复制助记词">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(text, '助记词')}
              />
            </Tooltip>
          </Space>
        ) : null
      ),
      hidden: !['EVM'].includes(selectedChain),
    },
  ];

  return (
    <div className="container-responsive">
      {/* 标题 */}
      <h1 style={{ 
        textAlign: 'center', 
                  fontSize: 'var(--font-xl)',
          fontWeight: 'bold',
          marginBottom: 'var(--spacing-2xl)',
        color: '#333'
      }}>
        批量生成 {selectedChain} 钱包地址
      </h1>

                      <Card className="spacing-responsive">
          {/* 选择链 */}
          <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                      <h3 style={{ marginBottom: 'var(--spacing-md)', fontSize: 'var(--font-md)', fontWeight: '500' }}>
              选择批量生成钱包的链
            </h3>
          <Space wrap>
            {chainOptions.map(chain => (
              <Button
                key={chain}
                type={selectedChain === chain ? 'primary' : 'default'}
                onClick={() => {
                  setSelectedChain(chain);
                  setWallets([]); // 重置钱包状态
                }}
                style={{ 
                  ...buttonStyle,
                  minWidth: chain.includes('TESTNET') ? '120px' : '80px',
                  opacity: !['EVM', 'SOL', 'SUI', 'APTOS'].includes(chain) ? 0.6 : 1
                }}
                disabled={!['EVM', 'SOL', 'SUI', 'APTOS'].includes(chain)}
              >
                {chain}
              </Button>
            ))}
          </Space>
          
          <div style={{ 
            marginTop: '12px', 
            padding: '12px', 
            background: '#fff7e6', 
            border: '1px solid #ffd591',
            borderRadius: '6px',
            fontSize: '14px',
            color: '#d46b08'
          }}>
            <strong>Tips:</strong> 钱包生成过程均在本地电脑完成，我们无法获取到您的助记词及私钥！
          </div>

          <div style={{ 
            marginTop: 'var(--spacing-sm)', 
            fontSize: 'var(--font-sm)', 
            color: '#666'
          }}>
            {selectedChain === 'EVM' 
              ? 'EVM钱包兼容 eth,bsc,polygon,arb,op,base等基于以太坊协议的链'
              : selectedChain === 'SOL'
              ? 'SOL钱包支持 Solana 主网和测试网，兼容 Phantom、Solflare 等钱包'
              : selectedChain === 'SUI'
              ? 'SUI钱包支持 Sui 主网和测试网，兼容 Sui Wallet、Ethos Wallet 等钱包'
              : selectedChain === 'APTOS'
              ? 'APTOS钱包支持 Aptos 主网和测试网，兼容 Petra Wallet、Martian Wallet 等钱包'
              : ''
            }
          </div>
        </div>

        {/* 选择助记词长度 */}
        {selectedChain === 'EVM' && (
          <div style={{ marginBottom: '24px' }}>
                          <h3 style={{ marginBottom: 'var(--spacing-md)', fontSize: 'var(--font-md)', fontWeight: '500' }}>
                选择助记词长度
              </h3>
            <Space>
              {mnemonicLengthOptions.map(length => (
                <Button
                  key={length}
                  type={mnemonicLength === length ? 'primary' : 'default'}
                  onClick={() => setMnemonicLength(length)}
                  style={{ ...buttonStyle, minWidth: 'var(--btn-sm-width)', fontSize: 'var(--font-sm)' }}
                >
                  {length}位
                </Button>
              ))}
            </Space>
          </div>
        )}

        {/* 进程数和生成数量 */}
        <Row gutter={[32, 16]} style={{ marginBottom: '32px' }}>
          <Col xs={24} sm={12}>
            <div>
              <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '500' }}>
                进程数
              </h3>
              <Space.Compact style={{ width: '100%' }}>
                <Button
                  icon={<MinusOutlined />}
                  onClick={() => adjustValue(processCount, -1, 1, 20, setProcessCount)}
                  style={operationButtonStyle}
                />
                <div style={{ 
                  ...componentStyle,
                  padding: '0 16px', 
                  border: '1px solid #d9d9d9',
                  borderLeft: 'none',
                  borderRight: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  fontSize: '16px',
                  background: '#fafafa'
                }}>
                  {processCount}
                </div>
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => adjustValue(processCount, 1, 1, 20, setProcessCount)}
                  style={operationButtonStyle}
                />
              </Space.Compact>
              <div style={{ 
                marginTop: '8px', 
                fontSize: '12px', 
                color: '#999'
              }}>
                用于多进程并行生成钱包
              </div>
            </div>
          </Col>
          
          <Col xs={24} sm={12}>
            <div>
              <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '500' }}>
                生成的钱包地址数量
              </h3>
              <InputNumber
                value={walletCount}
                onChange={(value) => setWalletCount(value || 1)}
                min={1}
                max={10000}
                style={{ 
                  ...componentStyle,
                  width: '100%',
                }}
                placeholder="输入要生成的钱包数量"
              />
              <div style={{ 
                marginTop: '8px', 
                fontSize: '12px', 
                color: '#999'
              }}>
                建议一次生成不超过1000个
              </div>
            </div>
          </Col>
        </Row>

        {/* 操作按钮 */}
        <div style={{ textAlign: 'center' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Space>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={generateWallets}
                loading={loading}
                style={primaryButtonStyle}
              >
                立即生成
              </Button>
              {wallets.length > 0 && (
                <Button
                  onClick={exportToExcel}
                  icon={<DownloadOutlined />}
                  style={buttonStyle}
                >
                  下载表格
                </Button>
              )}
            </Space>
            {loading && (
              <>
                <Progress 
                  percent={progress} 
                  status="active"
                  style={{ maxWidth: 400, margin: '0 auto' }}
                />
                <div style={{ color: '#666', fontSize: '14px' }}>
                  已生成 {generatedCount} / {walletCount} 个钱包
                </div>
              </>
            )}
          </Space>
        </div>
      </Card>

      {/* 结果显示区域 */}
      {wallets.length > 0 && (
        <>
          <Divider>
            <span style={{ color: '#1890ff', fontSize: '16px' }}>
              已生成 {wallets.length} 个钱包
            </span>
          </Divider>
          
          <Table
            columns={columns}
            dataSource={wallets}
            rowKey="index"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 个钱包`,
            }}
            scroll={{ x: 1000 }}
            size="small"
            virtual
          />

          {/* 安全提示 */}
          <Card size="small" style={{ 
            background: '#fff2f0', 
            border: '1px solid #ffccc7',
            marginTop: '20px'
          }}>
            <div style={{ color: '#ff4d4f' }}>
              <strong>⚠️ 安全提醒：</strong>
              <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
                <li>请妥善保管生成的私钥和助记词，任何人获得这些信息都可以控制您的钱包</li>
                <li>建议在安全的离线环境中生成和使用钱包</li>
                <li>不要在不安全的网络环境中使用此功能</li>
                <li>导出的Excel文件包含敏感信息，请安全存储</li>
              </ul>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default BatchWallet; 